import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2, MessageSquare, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { COMPANY } from "../data/site";
import { CUSTOMER_TYPES, INQUIRY_TYPES } from "../data/faq";

const empty = {
    name: "", business_name: "", customer_type: "", inquiry_type: "", phone: "", email: "",
    city: "", state: "", interested_products: "", message: "",
};

export default function Contact() {
    const [form, setForm] = useState(empty);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.business_name || !form.phone || !form.email || !form.message) {
            toast.error("Please fill in name, business name, phone, email and message.");
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            setForm(empty);
            toast.success("Message sent! We'll be in touch shortly.");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const requestPriceList = () => {
        set("inquiry_type", "Request Price List");
        set("message", "I would like to request your full wholesale price list. Please send me current pricing and availability.");
        document.getElementById("contact-form-card")?.scrollIntoView({ behavior: "smooth" });
        toast.info("Price list inquiry ready — just add your details and submit.");
    };

    const inputCls = "w-full border border-border bg-transparent px-4 py-3 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

    return (
        <div data-testid="contact-page">
            <section className="bg-primary text-primary-foreground">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
                    <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-3">Get In Touch</p>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Contact YATTO LLC</h1>
                    <p className="text-primary-foreground/70 mt-3 max-w-xl">Questions about wholesale pricing, products, or partnerships? Our team is ready to help.</p>
                </div>
            </section>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-[1fr_1.2fr] gap-12">
                {/* Info */}
                <div>
                    <h2 className="font-heading font-bold text-2xl mb-8">YATTO LLC</h2>
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 bg-primary text-primary-foreground flex items-center justify-center shrink-0"><Mail size={18} /></div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</p>
                                <a href={`mailto:${COMPANY.email}`} className="font-semibold hover:text-primary transition-colors" data-testid="contact-email">{COMPANY.email}</a>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 bg-primary text-primary-foreground flex items-center justify-center shrink-0"><Phone size={18} /></div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Main Phone</p>
                                <a href={`tel:${COMPANY.phones[0]}`} className="block font-semibold text-lg hover:text-primary transition-colors" data-testid="contact-main-phone">{COMPANY.phones[0]}</a>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mt-4 mb-1">Additional Contacts</p>
                                <div className="space-y-1">
                                    {COMPANY.phones.slice(1).map((p) => (
                                        <a key={p} href={`tel:${p}`} className="block text-sm font-medium text-foreground/80 hover:text-primary transition-colors" data-testid={`contact-phone-${p}`}>{p}</a>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="w-11 h-11 bg-primary text-primary-foreground flex items-center justify-center shrink-0"><MapPin size={18} /></div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Distribution</p>
                                <p className="font-semibold">{COMPANY.serviceArea}</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-8">
                        <a href={`tel:${COMPANY.phones[0]}`} className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors" data-testid="call-text-btn">
                            <MessageSquare size={16} /> Call / Text Us
                        </a>
                        <button onClick={requestPriceList} className="flex items-center gap-2 border border-border px-5 py-3 text-sm font-semibold hover:border-primary transition-colors" data-testid="request-price-list-btn">
                            <FileText size={16} /> Request Price List
                        </button>
                    </div>
                </div>

                {/* Form */}
                <div id="contact-form-card" className="border border-border bg-card p-8">
                    {sent ? (
                        <div className="text-center py-12" data-testid="contact-success">
                            <CheckCircle2 size={56} className="text-primary mx-auto mb-5" />
                            <h3 className="font-heading font-bold text-2xl mb-2">Message Sent</h3>
                            <p className="text-muted-foreground">Thank you for reaching out. Our team will get back to you shortly.</p>
                            <button onClick={() => setSent(false)} className="mt-6 text-primary font-semibold">Send another message</button>
                        </div>
                    ) : (
                        <form onSubmit={submit} data-testid="contact-form">
                            <h2 className="font-heading font-bold text-2xl mb-2">Request Wholesale Information or Place an Order Inquiry</h2>
                            <p className="text-sm text-muted-foreground mb-6">Tell us what products you are interested in and our team will contact you to confirm pricing, availability, and delivery.</p>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</label><input className={inputCls} data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
                                <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Business Name *</label><input className={inputCls} data-testid="contact-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Customer Type</label>
                                    <select className={inputCls} data-testid="contact-customer-type" value={form.customer_type} onChange={(e) => set("customer_type", e.target.value)}>
                                        <option value="">Select type</option>
                                        {CUSTOMER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Inquiry Type</label>
                                    <select className={inputCls} data-testid="contact-inquiry-type" value={form.inquiry_type} onChange={(e) => set("inquiry_type", e.target.value)}>
                                        <option value="">Select inquiry</option>
                                        {INQUIRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Phone *</label><input className={inputCls} data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                                <div><label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</label><input type="email" className={inputCls} data-testid="contact-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                                <div><label className="text-xs uppercase tracking-wider text-muted-foreground">City</label><input className={inputCls} data-testid="contact-city" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                                <div><label className="text-xs uppercase tracking-wider text-muted-foreground">State</label><input className={inputCls} data-testid="contact-state" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                            </div>
                            <div className="mt-4"><label className="text-xs uppercase tracking-wider text-muted-foreground">Interested Products</label><input className={inputCls} data-testid="contact-products" placeholder="e.g. Olive oil, Chat Cola, snacks" value={form.interested_products} onChange={(e) => set("interested_products", e.target.value)} /></div>
                            <div className="mt-4"><label className="text-xs uppercase tracking-wider text-muted-foreground">Message *</label><textarea rows={5} className={inputCls} data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} /></div>
                            <button type="submit" disabled={sending} data-testid="contact-submit" className="mt-6 flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors disabled:opacity-60">
                                {sending ? "Sending..." : <>Send Message <Send size={16} /></>}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
